import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Heading,
  IconButton
} from '@sparrowengg/twigs-react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from '@sparrowengg/twigs-react-icons';
import { devPortalServices } from 'commons/services';

const AppStorage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'storage' | 'files' | 'schedules'>(
    'storage'
  );
  const [storageData, setStorageData] = useState<any[]>([]);
  const [filesData, setFilesData] = useState<any[]>([]);
  const [schedulesData, setSchedulesData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [appDetail, setAppDetail] = useState<any>(null);
  const [appDetailLoading, setAppDetailLoading] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const limit = 10; // Items per page
  // Cache to track if data has been fetched for each tab
  const dataFetchedRef = useRef<{
    storage: boolean;
    files: boolean;
    schedules: boolean;
  }>({
    storage: false,
    files: false,
    schedules: false
  });

  // Cache for app detail and storage data
  const appDetailCacheRef = useRef<Record<string, any>>({});
  const storageDataCacheRef = useRef<
    Record<string, { data: any[]; nextToken: string | null }>
  >({});

  // Fetch app detail to check isInstalled (with caching)
  useEffect(() => {
    const fetchAppDetail = async () => {
      if (!id) return;

      // Check cache first
      if (appDetailCacheRef.current[id]) {
        setAppDetail(appDetailCacheRef.current[id]);
        setAppDetailLoading(false);
        return;
      }

      setAppDetailLoading(true);
      try {
        const response = await devPortalServices.getSingleApp(id);
        // Cache the response
        appDetailCacheRef.current[id] = response;
        setAppDetail(response);
      } catch (error) {
        console.error('Error fetching app detail:', error);
      } finally {
        setAppDetailLoading(false);
      }
    };
    fetchAppDetail();
  }, [id]);

  const fetchStorageData = useCallback(
    async (isInitial = false) => {
      if (!appDetail || !appDetail.isInstalled) return;

      const cacheKey = `${appDetail.appId}_storage`;

      // Check cache for initial load
      if (isInitial && storageDataCacheRef.current[cacheKey]) {
        const cached = storageDataCacheRef.current[cacheKey];
        setStorageData(cached.data);
        setNextToken(cached.nextToken);
        return;
      }

      if (isInitial) {
        setLoading(true);
        isLoadingRef.current = true;
      } else {
        if (isLoadingRef.current || !nextToken) return; // Prevent multiple simultaneous loads
        setLoadingMore(true);
        isLoadingRef.current = true;
      }

      try {
        const tokenToFetch = isInitial ? null : nextToken;
        const response = await devPortalServices.getAppStorageUsage(
          appDetail.appId,
          limit,
          tokenToFetch
        );

        if (isInitial) {
          const items = response.items || [];
          setStorageData(items);
          // Cache the initial data
          storageDataCacheRef.current[cacheKey] = {
            data: items,
            nextToken: response.nextToken || null
          };
        } else {
          setStorageData((prev) => {
            const newData = [...prev, ...(response.items || [])];
            // Update cache with accumulated data
            storageDataCacheRef.current[cacheKey] = {
              data: newData,
              nextToken: response.nextToken || null
            };
            return newData;
          });
        }
        setNextToken(response.nextToken || null);
      } catch (error) {
        console.error('Error fetching storage data:', error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
        isLoadingRef.current = false;
      }
    },
    [appDetail, nextToken, limit]
  );

  const fetchFilesData = useCallback(async () => {
    if (!appDetail || !appDetail.isInstalled) return;
    setLoading(true);
    try {
      // TODO: Uncomment when API is ready
      // const response = await devPortalServices.getAppFilesUsage(
      //   appDetail.appId
      // );
      // setFilesData(response.data || []);

      // Mock data for now
      setFilesData([]);
    } catch (error) {
      console.error('Error fetching files data:', error);
    } finally {
      setLoading(false);
    }
  }, [appDetail]);

  const fetchSchedulesData = useCallback(async () => {
    if (!appDetail || !appDetail.isInstalled) return;
    setLoading(true);
    try {
      const response = await devPortalServices.getAppScheduleUsage(
        appDetail.appId
      );
      // Schedules API returns array directly, not wrapped in items
      setSchedulesData(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching schedules data:', error);
    } finally {
      setLoading(false);
    }
  }, [appDetail]);

  useEffect(() => {
    if (!appDetail) return;

    if (activeTab === 'storage') {
      // Only fetch if not already fetched (cache the first load)
      if (!dataFetchedRef.current.storage) {
        fetchStorageData(true);
        dataFetchedRef.current.storage = true;
      }
    }
    // Files section commented out
    // else if (activeTab === 'files') {
    //   // Only fetch if not already fetched
    //   if (!dataFetchedRef.current.files) {
    //     fetchFilesData();
    //     dataFetchedRef.current.files = true;
    //   }
    // }
    else if (activeTab === 'schedules') {
      // Only fetch if not already fetched
      if (!dataFetchedRef.current.schedules) {
        fetchSchedulesData();
        dataFetchedRef.current.schedules = true;
      }
    }
  }, [
    activeTab,
    appDetail,
    fetchStorageData,
    fetchFilesData,
    fetchSchedulesData
  ]);

  const handleScroll = useCallback(() => {
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scroll handler
    scrollTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container || isLoadingRef.current || !nextToken) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      // Trigger when user is within 200px of the bottom
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchStorageData(false);
      }
    }, 100); // 100ms debounce
  }, [nextToken, fetchStorageData]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container && activeTab === 'storage' && appDetail?.isInstalled) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, activeTab, appDetail]);

  const renderInstallationMessage = () => {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        css={{ padding: '$20', minHeight: '400px' }}
      >
        <Text size="md" css={{ color: '$neutral600', textAlign: 'center' }}>
          Please install the app first to see the app feature usage
        </Text>
      </Flex>
    );
  };

  const renderStorageContent = () => {
    if (appDetailLoading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading...
          </Text>
        </Flex>
      );
    }

    if (!appDetail || !appDetail.isInstalled) {
      return renderInstallationMessage();
    }

    if (loading && storageData.length === 0) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading storage data...
          </Text>
        </Flex>
      );
    }

    if (storageData.length === 0) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            No storage data available
          </Text>
        </Flex>
      );
    }

    return (
      <Box
        ref={scrollContainerRef}
        css={{
          padding: '$20',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }}
      >
        <Flex flexDirection="column" gap="$4">
          {storageData.map((item, index) => (
            <Box
              key={`${item.key}-${index}`}
              css={{
                border: '$borderWidths$xs solid $neutral200',
                borderRadius: '$md',
                padding: '$6',
                backgroundColor: '$white900',
                '&:hover': {
                  backgroundColor: '$neutral50'
                }
              }}
            >
              <Flex alignItems="flex-start" gap="$4">
                <Text
                  size="sm"
                  weight="medium"
                  css={{
                    color: '$neutral700',
                    minWidth: '150px',
                    fontFamily: 'monospace'
                  }}
                >
                  {item.key}:
                </Text>
                <Text
                  size="sm"
                  css={{
                    color: '$neutral900',
                    flex: 1,
                    wordBreak: 'break-word',
                    fontFamily: 'monospace'
                  }}
                >
                  {typeof item.value === 'object' && Array.isArray(item.value)
                    ? JSON.stringify(item.value, null, 2)
                    : typeof item.value === 'object'
                      ? JSON.stringify(item.value, null, 2)
                      : String(item.value)}
                </Text>
              </Flex>
            </Box>
          ))}
        </Flex>
        {loadingMore && (
          <Flex
            alignItems="center"
            justifyContent="center"
            css={{ padding: '$6', marginTop: '$4' }}
          >
            <Text size="sm" css={{ color: '$neutral600' }}>
              Loading more...
            </Text>
          </Flex>
        )}
      </Box>
    );
  };

  const renderFilesContent = () => {
    if (appDetailLoading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading...
          </Text>
        </Flex>
      );
    }

    if (!appDetail || !appDetail.isInstalled) {
      return renderInstallationMessage();
    }

    if (loading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading files data...
          </Text>
        </Flex>
      );
    }

    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        css={{ padding: '$20', minHeight: '400px' }}
      >
        <Text size="md" css={{ color: '$neutral600' }}>
          Feature to be released
        </Text>
      </Flex>
    );
  };

  const renderSchedulesContent = () => {
    if (appDetailLoading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading...
          </Text>
        </Flex>
      );
    }

    if (!appDetail || !appDetail.isInstalled) {
      return renderInstallationMessage();
    }

    if (loading) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            Loading schedules data...
          </Text>
        </Flex>
      );
    }

    if (schedulesData.length === 0) {
      return (
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{ padding: '$20', minHeight: '400px' }}
        >
          <Text size="md" css={{ color: '$neutral600' }}>
            No schedules data available
          </Text>
        </Flex>
      );
    }

    return (
      <Box
        css={{
          padding: '$20',
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto'
        }}
      >
        <Flex flexDirection="column" gap="$4">
          {schedulesData.map((item, index) => (
            <Box
              key={`${item.name}-${index}`}
              css={{
                border: '$borderWidths$xs solid $neutral200',
                borderRadius: '$md',
                padding: '$6',
                backgroundColor: '$white900',
                '&:hover': {
                  backgroundColor: '$neutral50'
                }
              }}
            >
              <Flex flexDirection="column" gap="$4">
                {/* Schedule Name */}
                <Text
                  size="md"
                  weight="medium"
                  css={{
                    color: '$neutral900',
                    marginBottom: '$2'
                  }}
                >
                  {item.name}
                </Text>
                {/* Payload Data */}
                {item.payload?.data && (
                  <Flex flexDirection="column" gap="$2">
                    {Object.entries(item.payload.data).map(([key, value]) => (
                      <Flex key={key} alignItems="flex-start" gap="$4">
                        <Text
                          size="sm"
                          weight="medium"
                          css={{
                            color: '$neutral700',
                            minWidth: '150px',
                            fontFamily: 'monospace'
                          }}
                        >
                          {key}:
                        </Text>
                        <Text
                          size="sm"
                          css={{
                            color: '$neutral900',
                            flex: 1,
                            wordBreak: 'break-word',
                            fontFamily: 'monospace'
                          }}
                        >
                          {typeof value === 'object'
                            ? JSON.stringify(value, null, 2)
                            : String(value)}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Flex>
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };

  return (
    <Box
      css={{
        height: 'calc(100vh - 80px)',
        overflowY: 'auto',
        padding: '$20'
      }}
    >
      <Flex justifyContent="center">
        <Box css={{ maxWidth: 1200, width: '100%' }}>
          <Flex
            flexDirection="column"
            css={{
              paddingBlock: '$5'
            }}
          >
            <Flex
              alignItems="center"
              gap="$4"
              css={{
                marginBottom: '$8',
                position: 'relative'
              }}
            >
              <IconButton
                size="lg"
                variant="ghost"
                icon={<ChevronLeftIcon color="#56B0BB" />}
                onClick={() => navigate(`/edit-local-dev-app/${id}`)}
                css={{ position: 'absolute', left: '-$25', top: -2 }}
              />
              <Flex flexDirection="column" gap="$2">
                <Heading size="h5">App Feature Usage</Heading>
              </Flex>
            </Flex>
            <Tabs
              value={activeTab}
              onValueChange={(value) =>
                setActiveTab(value as 'storage' | 'files' | 'schedules')
              }
            >
              <Flex
                justifyContent="center"
                css={{
                  borderBottom: '$borderWidths$xs solid $neutral200',
                  marginBottom: '$8'
                }}
              >
                <TabsList>
                  <TabsTrigger value="storage" css={{ padding: '$6 $8' }}>
                    <Text size="md" weight="medium">
                      Storage
                    </Text>
                  </TabsTrigger>
                  {/* Files section commented out */}
                  {/* <TabsTrigger value="files" css={{ padding: '$6 $8' }}>
                    <Text size="md" weight="medium">
                      Files
                    </Text>
                  </TabsTrigger> */}
                  <TabsTrigger value="schedules" css={{ padding: '$6 $8' }}>
                    <Text size="md" weight="medium">
                      Schedules
                    </Text>
                  </TabsTrigger>
                </TabsList>
              </Flex>

              <TabsContent value="storage">
                {renderStorageContent()}
              </TabsContent>
              {/* Files section commented out */}
              {/* <TabsContent value="files">{renderFilesContent()}</TabsContent> */}
              <TabsContent value="schedules">
                {renderSchedulesContent()}
              </TabsContent>
            </Tabs>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default AppStorage;
